"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { ChevronDown, Menu } from "lucide-react"

import { HeavySafeLink } from "@/components/site/heavy-safe-link"
import { PortalNavMenuItems } from "@/components/site/portal-nav-menu-items"
import { usePortalChrome } from "@/components/site/portal-chrome-context"
import { SiteLogo } from "@/components/site/site-logo"
import { SiteAuthControls } from "@/components/site/site-auth-controls"
import { buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SITE_NAV, SITE_SERVICES } from "@/lib/site-nav"
import type { PortalNavItem } from "@/lib/portal-content"
import { cn } from "@/lib/utils"

function navLinkClass(active: boolean) {
  return cn(
    "cursor-pointer px-1 py-1.5 text-sm font-medium whitespace-nowrap",
    "border-b-2 border-transparent transition-[color,border-color]",
    active
      ? "border-primary text-foreground"
      : "text-muted-foreground hover:border-primary hover:text-foreground",
  )
}

function isActiveHref(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/"
  return pathname === href || pathname.startsWith(`${href}/`)
}

function NavLink({ item, pathname }: { item: PortalNavItem; pathname: string }) {
  const active = isActiveHref(pathname, item.href)
  const className = navLinkClass(active)
  const isHeavy = item.href.startsWith("/labels") || item.href.startsWith("/km")

  if (isHeavy) {
    return (
      <HeavySafeLink href={item.href} className={className}>
        {item.title}
      </HeavySafeLink>
    )
  }

  return (
    <Link
      href={item.href}
      prefetch={false}
      target={item.target === "_blank" ? "_blank" : undefined}
      rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
      className={className}
    >
      {item.title}
    </Link>
  )
}

function fallbackNav(): PortalNavItem[] {
  return [
    { id: "home", title: SITE_NAV.home.label, href: SITE_NAV.home.href, children: [] },
    { id: "labels", title: SITE_NAV.labels.label, href: SITE_NAV.labels.href, children: [] },
    { id: "km", title: SITE_NAV.km.label, href: SITE_NAV.km.href, children: [] },
    {
      id: "services",
      title: "Сервисы",
      href: "/services",
      children: SITE_SERVICES.map((s, i) => ({
        id: `svc-${i}`,
        title: s.label,
        href: s.href,
      })),
    },
    { id: "support", title: SITE_NAV.support.label, href: SITE_NAV.support.href, children: [] },
  ]
}

export function SiteHeader() {
  const pathname = usePathname()
  const { navigation } = usePortalChrome()
  const items = (navigation.length > 0 ? navigation : fallbackNav()).filter(
    (item) => item.is_visible !== false,
  )

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/80">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6 md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <SiteLogo className="text-base sm:text-lg md:justify-self-start" />

        <nav
          className="hidden items-center justify-center gap-5 md:flex"
          aria-label="Главное меню"
        >
          {items.map((item) => {
            const children = (item.children ?? []).filter(
              (child) => child.is_visible !== false,
            )
            if (children.length > 0) {
              const active =
                isActiveHref(pathname, item.href) ||
                children.some((child) => isActiveHref(pathname, child.href))
              return (
                <DropdownMenu key={item.id}>
                  <DropdownMenuTrigger
                    className={cn(
                      navLinkClass(active),
                      "inline-flex items-center gap-1 outline-none",
                    )}
                  >
                    {item.title}
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="min-w-56">
                    {children.map((child) => (
                      <DropdownMenuItem
                        key={child.id}
                        render={
                          <Link
                            href={child.href}
                            prefetch={false}
                            target={child.target === "_blank" ? "_blank" : undefined}
                          />
                        }
                      >
                        {child.title}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }

            return <NavLink key={item.id} item={item} pathname={pathname} />
          })}
        </nav>

        <div className="ml-auto flex items-center justify-end gap-2 md:ml-0 md:justify-self-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                buttonVariants({ variant: "outline", size: "icon-sm" }),
                "md:hidden",
              )}
              aria-label="Открыть меню"
            >
              <Menu className="h-4 w-4 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              <PortalNavMenuItems items={items} />
              <DropdownMenuSeparator />
              <SiteAuthControls mobile />
            </DropdownMenuContent>
          </DropdownMenu>

          <SiteAuthControls />
        </div>
      </div>
    </header>
  )
}
